/**
 * Merges existing visible columns preferences with default columns
 * This ensures that when new columns are added to defaultVisibleColumns,
 * they are included in the preferences while preserving existing user choices.
 * It also removes columns that are no longer in defaultVisibleColumns.
 * 
 * @param {Object} existingPreferences - Current preferences from state
 * @param {Object} defaultVisibleColumns - Default column configuration
 * @returns {Object} Merged visible columns object
 */
export const mergeVisibleColumns = (existingPreferences, defaultVisibleColumns) => {
  if (!existingPreferences) {
    return defaultVisibleColumns;
  }
  
  // Start with default columns
  const mergedColumns = { ...defaultVisibleColumns };
  
  // Override with existing preferences, but only for columns that still exist in defaults
  Object.keys(existingPreferences).forEach(key => {
    if (key in defaultVisibleColumns) {
      mergedColumns[key] = existingPreferences[key];
    }
    // If the key doesn't exist in defaultVisibleColumns, it's ignored (removed)
  });
  
  return mergedColumns;
};

/**
 * Checks if existing preferences are missing any default columns
 * 
 * @param {Object} existingPreferences - Current preferences from state
 * @param {Object} defaultVisibleColumns - Default column configuration
 * @returns {Array} Array of missing column keys
 */
export const getMissingColumns = (existingPreferences, defaultVisibleColumns) => {
  if (!existingPreferences) {
    return Object.keys(defaultVisibleColumns);
  }
  
  return Object.keys(defaultVisibleColumns).filter(
    key => !(key in existingPreferences)
  );
};

/**
 * Checks if existing preferences have any columns that are no longer in defaultVisibleColumns
 * 
 * @param {Object} existingPreferences - Current preferences from state
 * @param {Object} defaultVisibleColumns - Default column configuration
 * @returns {Array} Array of removed column keys
 */
export const getRemovedColumns = (existingPreferences, defaultVisibleColumns) => {
  if (!existingPreferences) {
    return [];
  }
  
  return Object.keys(existingPreferences).filter(
    key => !(key in defaultVisibleColumns)
  );
};

/**
 * Checks if preferences need to be updated (either missing new columns or have removed columns)
 * 
 * @param {Object} existingPreferences - Current preferences from state
 * @param {Object} defaultVisibleColumns - Default column configuration
 * @returns {boolean} True if preferences need updating
 */
export const needsPreferencesUpdate = (existingPreferences, defaultVisibleColumns) => {
  if (!existingPreferences) {
    return true;
  }
  
  const missingColumns = getMissingColumns(existingPreferences, defaultVisibleColumns);
  const removedColumns = getRemovedColumns(existingPreferences, defaultVisibleColumns);
  
  return missingColumns.length > 0 || removedColumns.length > 0;
};

